import React, { useState, useEffect } from "react";
import { Check, Utensils, Printer, Copy, RefreshCw, Loader2, Undo2, ChevronDown, PenLine, Plus, Files } from "lucide-react";
import { SectionHeading, StepInstructions } from "@/components/wizard/ui";
import { DEMAND_COLOR } from "@/lib/planModel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import { toast } from "sonner";
import BatchPrepCard from "@/components/steps/BatchPrepCard";

const SLOT_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack", fuel: "Training Fuel" };

// Collapsible container for each day's two pathways — expand/collapse the whole
// day like the Guide page so scrolling a long week is one tap, not five swipes.
function PathwayDayCollapse({ dp, sel, defaultOpen, soloDay, onCopy, onDuplicate, onPrint, onSelect, onSwap, onRevert }) {
  const [open, setOpen] = useState(defaultOpen);
  const path = dp.pathways[sel];
  const t = path?.totals;
  const hiddenForPrint = soloDay !== null && soloDay !== dp.index;
  return (
    <div data-testid={`pathway-day-${dp.index}`} className={`border border-white/10 bg-[#141414] flex overflow-hidden ${hiddenForPrint ? "no-print" : ""}`}>
      <div className="w-1.5 shrink-0" style={{ background: DEMAND_COLOR[dp.demand] || "#71717A" }} aria-hidden />
      <div className="flex-1">
      <div className="px-3 sm:px-4 py-3">
        <div className="flex items-center gap-2">
          <button type="button" data-testid={`pathway-day-toggle-${dp.index}`} onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-90 transition-opacity">
            <ChevronDown className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
            <span className="font-display font-bold text-lg sm:text-xl uppercase text-white truncate">{dp.day}</span>
            {!open && t && (
              <span className="hidden md:inline text-[10px] font-mono text-zinc-400 truncate">Pathway {sel} · {t.kcal}kcal · {t.protein}P {t.carb}C {t.fat}F</span>
            )}
          </button>
          <div className="flex gap-1 no-print shrink-0">
            <button data-testid={`duplicate-day-${dp.index}`} onClick={onDuplicate} title="Copy these meals to other days"
              className="p-1.5 border border-white/15 text-zinc-200 hover:text-[#E2E30A] hover:border-[#E2E30A]/60 transition-colors">
              <Files className="h-3.5 w-3.5" />
            </button>
            <button data-testid={`copy-day-${dp.index}`} onClick={onCopy} title="Copy day menu as text"
              className="p-1.5 border border-white/15 text-zinc-200 hover:text-white hover:border-white/40 transition-colors">
              <Copy className="h-3.5 w-3.5" />
            </button>
            <button data-testid={`print-day-${dp.index}`} onClick={onPrint} title="Print just this day"
              className="p-1.5 border border-white/15 text-zinc-200 hover:text-white hover:border-white/40 transition-colors">
              <Printer className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1.5 pl-7 flex-wrap">
          <span className="text-[10px] uppercase px-2 py-0.5 tracking-[0.15em] font-semibold shrink-0" style={{ color: "#F5F1E8", border: `1px solid ${DEMAND_COLOR[dp.demand]}` }}>{dp.isRace ? "Race" : dp.demand.replace("_", " ")}</span>
          <span className="text-[11px] text-zinc-200 font-mono">Target {dp.target.energy}kcal</span>
        </div>
      </div>
      {open && (
        <div className="px-3 sm:px-4 pb-4 pt-1 border-t border-white/5" data-testid={`pathway-day-body-${dp.index}`}>
          {dp.scaleCapped && (
            <div data-testid={`pathway-shortfall-${dp.index}`}
              className="mb-3 border-l-2 border-[#FF9500] bg-[#FF9500]/10 px-3 py-2 text-xs text-[#FFB454]">
              Big fuelling day — this target is higher than everyday meals can hold. Close the
              {dp.shortfallKcal ? ` ~${dp.shortfallKcal} kcal` : ""} gap with in-session fuel and larger recovery portions.
              {dp.fuelPlan && (
                <div className="mt-2 pt-2 border-t border-[#FF9500]/20 text-zinc-200">
                  <span className="uppercase tracking-wider text-[10px] text-[#FF9500]">Suggested in-session fuel</span>
                  <ul className="mt-1 space-y-0.5">
                    {dp.fuelPlan.items.map((it) => (
                      <li key={it.name} className="flex justify-between font-mono">
                        <span>{it.count} × {it.name}</span>
                        <span className="text-zinc-200">{it.kcal}kcal · {it.carb}g C</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 font-mono text-[#34C759]">≈ {dp.fuelPlan.kcal} kcal · {dp.fuelPlan.carb}g carbs added</p>
                </div>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <PathwayCard dp={dp} key_="A" selected={sel === "A"} onSelect={onSelect} onSwap={onSwap} onRevert={onRevert} />
            <PathwayCard dp={dp} key_="B" selected={sel === "B"} onSelect={onSelect} onSwap={onSwap} onRevert={onRevert} />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function PathwayCard({ dp, key_, selected, onSelect, onSwap, onRevert }) {
  const path = dp.pathways[key_];
  if (!path) return null;
  const t = path.totals;
  const swapped = !!path._original;
  return (
    <div data-testid={`pathway-card-${dp.index}-${key_}`} onClick={() => onSelect(dp.index, key_)}
      className={`cursor-pointer border bg-[#0A0A0A] p-4 transition-colors ${selected ? "border-[#4C9AFF]" : "border-white/10 hover:border-white/25 no-print"}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <button data-testid={`pathway-select-${dp.index}-${key_}`} onClick={(e) => { e.stopPropagation(); onSelect(dp.index, key_); }}
          className="flex-1 text-left flex items-center justify-between">
          <span className={`font-display font-bold text-lg uppercase ${selected ? "text-[#4C9AFF]" : "text-white"}`}>Pathway {key_}</span>
          {selected && <span className="inline-flex items-center gap-1 text-[10px] uppercase text-[#4C9AFF]"><Check className="h-3 w-3" /> Selected</span>}
        </button>
        {swapped && (
          <button data-testid={`revert-pathway-${dp.index}-${key_}`} onClick={(e) => { e.stopPropagation(); onRevert(dp, key_); }}
            title="Revert to the original meals"
            className="no-print inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-300 hover:text-[#E2E30A] border border-white/15 hover:border-[#E2E30A]/50 px-2 py-1 transition-colors">
            <Undo2 className="h-3 w-3" /> Revert
          </button>
        )}
      </div>
      <div className="space-y-2 mb-3">
        {path.meals.map((m, idx) => (
          <div key={`${m.slot}-${idx}`} className="border-b border-white/5 pb-2 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-zinc-200">{SLOT_LABEL[m.slot]}{/\d$/.test(m.slotKey || "") ? ` ${(m.slotKey || "").slice(-1)}` : ""}</p>
              <p className="text-sm text-white">{m.name}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">{(m.ingredients || []).map((ing) => `${ing.name} ${ing.qty}${ing.unit}`).join(" · ")}</p>
              <p className="text-[10px] text-zinc-200 font-mono">{Math.round(m.kcal)}kcal · {Math.round(m.protein)}P {Math.round(m.carb)}C {Math.round(m.fat)}F</p>
            </div>
            <button data-testid={`swap-meal-${dp.index}-${key_}-${m.slot}-${idx}`} title="Swap this meal or add your own food"
              onClick={(e) => { e.stopPropagation(); onSwap(dp, key_, m); }}
              className="no-print shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-white/20 text-zinc-100 hover:text-black hover:bg-[#E2E30A] hover:border-[#E2E30A] transition-colors">
              <RefreshCw className="h-3 w-3" /> Swap / Own
            </button>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs font-mono border-t border-white/10 pt-2">
        <span className="text-zinc-200">TOTAL</span>
        <span className="text-white font-bold">{t.kcal}kcal · <span className="text-[#22D3EE] font-bold">{t.protein}P</span> <span className="text-[#E2E30A] font-bold">{t.carb}C</span> <span className="text-[#FB7185] font-bold">{t.fat}F</span></span>
      </div>
    </div>
  );
}

export default function Step6Pathways({ plan, setPlan }) {
  const { generated, selections, dietary } = plan;
  const dayPathways = generated?.dayPathways;

  const select = (idx, key) => setPlan((p) => ({ ...p, selections: { ...p.selections, [idx]: key } }));

  const [soloDay, setSoloDay] = useState(null);
  const [swapCtx, setSwapCtx] = useState(null); // {dp, pathKey, meal}
  const [alts, setAlts] = useState([]);
  const [loadingAlts, setLoadingAlts] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customFood, setCustomFood] = useState({ name: "", kcal: "", protein: "", carb: "", fat: "" });
  // Duplicate-day dialog: pick a source day (dp), then tick target days to
  // copy that day's meals into. Beta feedback: "make Monday, Tues, Wed the
  // same meals rather than changing all individually".
  const [dupCtx, setDupCtx] = useState(null); // { source: dp, targets: Set<int> }

  useEffect(() => {
    const reset = () => setSoloDay(null);
    window.addEventListener("afterprint", reset);
    return () => window.removeEventListener("afterprint", reset);
  }, []);

  const printDay = (idx) => { setSoloDay(idx); setTimeout(() => window.print(), 120); };

  const openSwap = async (dp, pathKey, meal) => {
    setSwapCtx({ dp, pathKey, meal });
    setAlts([]);
    setCustomOpen(false);
    setCustomFood({ name: "", kcal: "", protein: "", carb: "", fat: "" });
    setLoadingAlts(true);
    try {
      const usedIds = (dp.pathways[pathKey].meals || []).map((m) => m.id);
      const { data } = await api.post("/engine/meal-alternatives", {
        dietary, slot: meal.slot, excludeIds: usedIds,
      });
      setAlts(data.alternatives || []);
    } catch {
      toast.error("Couldn't load alternatives — please try again");
    } finally {
      setLoadingAlts(false);
    }
  };

  const doSwap = async (newMealId) => {
    if (!swapCtx) return;
    const { dp, pathKey, meal } = swapCtx;
    setSwapping(true);
    try {
      const { data } = await api.post("/engine/swap-meal", {
        dietary,
        dayTarget: dp.target,
        currentMeals: dp.pathways[pathKey].meals,
        slot: meal.slotKey || meal.slot,
        newMealId,
      });
      setPlan((p) => {
        const next = p.generated.dayPathways.map((d) => {
          if (d.index !== dp.index) return d;
          const cur = d.pathways[pathKey];
          const _original = cur._original || { meals: cur.meals, totals: cur.totals };
          return { ...d, pathways: { ...d.pathways, [pathKey]: { ...data, _original } } };
        });
        return { ...p, generated: { ...p.generated, dayPathways: next } };
      });
      toast.success("Meal swapped — portions rebalanced to hit your target");
      setSwapCtx(null);
    } catch {
      toast.error("Swap failed — please try again");
    } finally {
      setSwapping(false);
    }
  };

  const doSwapCustom = () => {
    if (!swapCtx) return;
    const kcal = Number(customFood.kcal) || 0;
    if (!customFood.name.trim() || kcal <= 0) { toast.error("Add a name and at least the calories"); return; }
    const { dp, pathKey, meal } = swapCtx;
    const custom = {
      id: `custom-${Date.now()}`,
      slot: meal.slot,
      slotKey: meal.slotKey || meal.slot,
      name: customFood.name.trim(),
      kcal,
      protein: Number(customFood.protein) || 0,
      carb: Number(customFood.carb) || 0,
      fat: Number(customFood.fat) || 0,
      ingredients: [{ name: customFood.name.trim(), qty: 1, unit: "serving" }],
    };
    setPlan((p) => {
      const next = p.generated.dayPathways.map((d) => {
        if (d.index !== dp.index) return d;
        const cur = d.pathways[pathKey];
        const _original = cur._original || { meals: cur.meals, totals: cur.totals };
        const newMeals = cur.meals.map((m) => ((m.slotKey || m.slot) === (meal.slotKey || meal.slot) ? custom : m));
        const totals = newMeals.reduce((a, m) => ({
          kcal: a.kcal + Math.round(m.kcal || 0),
          protein: a.protein + Math.round(m.protein || 0),
          carb: a.carb + Math.round(m.carb || 0),
          fat: a.fat + Math.round(m.fat || 0),
        }), { kcal: 0, protein: 0, carb: 0, fat: 0 });
        return { ...d, pathways: { ...d.pathways, [pathKey]: { meals: newMeals, totals, capped: cur.capped, _original } } };
      });
      return { ...p, generated: { ...p.generated, dayPathways: next } };
    });
    toast.success("Custom food swapped in");
    setSwapCtx(null);
  };

  const revertPath = (dp, pathKey) => {
    setPlan((p) => {
      const next = p.generated.dayPathways.map((d) => {
        if (d.index !== dp.index) return d;
        const cur = d.pathways[pathKey];
        if (!cur._original) return d;
        return { ...d, pathways: { ...d.pathways, [pathKey]: { meals: cur._original.meals, totals: cur._original.totals, capped: false } } };
      });
      return { ...p, generated: { ...p.generated, dayPathways: next } };
    });
    toast.success(`Pathway ${pathKey} restored to the original meals`);
  };

  const openDuplicate = (dp) => setDupCtx({ source: dp, targets: new Set() });

  const toggleDupTarget = (idx) => setDupCtx((c) => {
    if (!c) return c;
    const next = new Set(c.targets);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    return { ...c, targets: next };
  });

  const applyDuplicate = () => {
    if (!dupCtx || dupCtx.targets.size === 0) return;
    const { source, targets } = dupCtx;
    setPlan((p) => {
      const srcDay = p.generated.dayPathways.find((d) => d.index === source.index);
      if (!srcDay) return p;
      // Snapshot both pathways as-is (meals + totals). We deliberately don't
      // rescale to the target day's energy — the beta tester wants literally
      // the same meals to eat, not the same recipes at different portions.
      const cloneCurrent = { ...srcDay.pathways.A };
      const cloneOther = { ...srcDay.pathways.B };
      const next = p.generated.dayPathways.map((d) => {
        if (!targets.has(d.index)) return d;
        // Keep _original so the Revert button still restores the original
        // engine-generated meals for that day.
        const keepOrigA = d.pathways.A?._original || { meals: d.pathways.A?.meals, totals: d.pathways.A?.totals };
        const keepOrigB = d.pathways.B?._original || { meals: d.pathways.B?.meals, totals: d.pathways.B?.totals };
        return {
          ...d,
          pathways: {
            A: cloneCurrent ? { ...cloneCurrent, _original: keepOrigA } : d.pathways.A,
            B: cloneOther ? { ...cloneOther, _original: keepOrigB } : d.pathways.B,
          },
        };
      });
      return { ...p, generated: { ...p.generated, dayPathways: next } };
    });
    // Also carry the source day's selected pathway over so the user's chosen
    // route (A or B) matches what they copied.
    const srcSel = selections[source.index] || "A";
    setPlan((p) => {
      const nextSel = { ...p.selections };
      targets.forEach((idx) => { nextSel[idx] = srcSel; });
      return { ...p, selections: nextSel };
    });
    toast.success(`${source.day}'s meals copied to ${dupCtx.targets.size} day${dupCtx.targets.size > 1 ? "s" : ""}. Use Undo on each pathway to revert.`);
    setDupCtx(null);
  };

  const copyDay = async (dp) => {
    const key = selections[dp.index] || "A";
    const path = dp.pathways[key];
    if (!path) return;
    const lines = [
      `${dp.day} — ${dp.isRace ? "Race day" : dp.demand.replace("_", " ")} · Target ${dp.target.energy} kcal`,
      `Pathway ${key}`,
      ...path.meals.map((m) => `• ${SLOT_LABEL[m.slot]}: ${m.name} — ${(m.ingredients || []).map((ing) => `${ing.name} ${ing.qty}${ing.unit}`).join(", ")} (${Math.round(m.kcal)} kcal · ${Math.round(m.protein)}P ${Math.round(m.carb)}C ${Math.round(m.fat)}F)`),
      `Total: ${path.totals.kcal} kcal · ${path.totals.protein}P ${path.totals.carb}C ${path.totals.fat}F`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success(`${dp.day} menu copied to clipboard`);
    } catch {
      toast.error("Copy failed — please try again");
    }
  };

  if (!dayPathways) {
    return (
      <div>
        <SectionHeading eyebrow="Step 07" title="Meal Pathways" />
        <div className="border border-dashed border-white/15 p-12 text-center">
          <Utensils className="h-6 w-6 text-zinc-200 mx-auto mb-3" />
          <p className="text-zinc-200">Generate your plan in Step 6 to build meal pathways.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PrintHeader title="Weekly Menu" subtitle={plan.name} />
      <div className="flex items-start justify-between gap-4 flex-wrap no-print">
        <SectionHeading eyebrow="Step 07" title="Meal Pathways"
          desc="Two exclusion-safe pathways per day, portioned to hit each day's energy target. Pick your preferred route." />
        <Button data-testid="print-menu-btn" onClick={() => window.print()} variant="outline"
          className="no-print rounded-none border-white/15 bg-transparent text-white hover:bg-white/5 uppercase text-xs tracking-wide">
          <Printer className="h-3.5 w-3.5 mr-2" /> Print / PDF
        </Button>
      </div>

      <StepInstructions>
        For every day, compare <span className="text-[#E2E30A]">Pathway A</span> and <span className="text-[#E2E30A]">Pathway B</span> and tap the one you prefer. Both hit the same daily targets and respect your exclusions — your choices feed the shopping list.
      </StepInstructions>
      <BatchPrepCard plan={plan} />
      <div className="space-y-6">
        {dayPathways.map((dp, dayIdx) => {
          const sel = selections[dp.index] || "A";
          return (
            <PathwayDayCollapse key={dp.index} dp={dp} sel={sel} defaultOpen={dayIdx === 0}
              soloDay={soloDay}
              onCopy={() => copyDay(dp)} onDuplicate={() => openDuplicate(dp)} onPrint={() => printDay(dp.index)}
              onSelect={select} onSwap={openSwap} onRevert={revertPath} />
          );
        })}
      </div>

      {/* Duplicate-day dialog — clone the source day's meals into other days. */}
      <Dialog open={!!dupCtx} onOpenChange={(o) => !o && setDupCtx(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/15 text-white max-w-md" data-testid="duplicate-day-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide text-white">
              Copy {dupCtx?.source?.day} meals to…
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-400 -mt-1">Tick the days you want to eat the same meals on. Meals and portions carry over exactly as chosen — targets will show any deviation. You can restore any day later with the Revert arrow on each pathway.</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {dayPathways.filter((d) => d.index !== dupCtx?.source?.index).map((d) => {
              const checked = !!dupCtx?.targets.has(d.index);
              return (
                <button key={d.index} type="button" data-testid={`dup-target-${d.index}`}
                  onClick={() => toggleDupTarget(d.index)}
                  className={`flex items-center gap-2 border px-3 py-2 text-left transition-colors ${checked ? "border-[#E2E30A] bg-[#E2E30A]/10 text-white" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                  <span className={`grid place-items-center h-4 w-4 border ${checked ? "border-[#E2E30A] bg-[#E2E30A] text-black" : "border-white/25"}`}>
                    {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="text-sm font-semibold">{d.day}</span>
                  <span className="ml-auto text-[10px] font-mono text-zinc-300 uppercase">{d.isRace ? "race" : d.demand.replace("_", " ")}</span>
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 mt-4">
            <Button data-testid="dup-cancel" onClick={() => setDupCtx(null)} variant="outline"
              className="flex-1 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase">
              Cancel
            </Button>
            <Button data-testid="dup-apply" onClick={applyDuplicate} disabled={!dupCtx || dupCtx.targets.size === 0}
              className="flex-1 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold disabled:opacity-40">
              Copy to {dupCtx?.targets.size || 0} day{(dupCtx?.targets.size || 0) === 1 ? "" : "s"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!swapCtx} onOpenChange={(o) => !o && setSwapCtx(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/15 text-white max-w-lg" data-testid="swap-meal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide text-white">
              Swap {swapCtx ? SLOT_LABEL[swapCtx.meal.slot] : ""}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-400 -mt-1">Pick an alternative — portions rebalance to keep your daily target. Or add your own food.</p>
          <div className="flex gap-2 mt-2">
            <button type="button" data-testid="swap-mode-alts" onClick={() => setCustomOpen(false)}
              className={`flex-1 px-3 py-1.5 text-[11px] uppercase tracking-wide border transition-colors ${!customOpen ? "border-[#E2E30A] text-[#E2E30A] bg-[#E2E30A]/10" : "border-white/15 text-zinc-300 hover:text-white"}`}>
              <RefreshCw className="h-3.5 w-3.5 inline mr-1" /> Alternatives
            </button>
            <button type="button" data-testid="swap-mode-custom" onClick={() => setCustomOpen(true)}
              className={`flex-1 px-3 py-1.5 text-[11px] uppercase tracking-wide border transition-colors ${customOpen ? "border-[#E2E30A] text-[#E2E30A] bg-[#E2E30A]/10" : "border-white/15 text-zinc-300 hover:text-white"}`}>
              <PenLine className="h-3.5 w-3.5 inline mr-1" /> Add my own
            </button>
          </div>
          {customOpen ? (
            <div className="mt-3 space-y-2" data-testid="swap-custom-form">
              <Input data-testid="swap-custom-name" placeholder="Food name (e.g. My protein oats)"
                value={customFood.name} onChange={(e) => setCustomFood({ ...customFood, name: e.target.value })}
                className="bg-black border-white/15 text-white" />
              <div className="grid grid-cols-4 gap-2">
                {[["kcal", "kcal"], ["protein", "P (g)"], ["carb", "C (g)"], ["fat", "F (g)"]].map(([k, lbl]) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase text-zinc-400">{lbl}</label>
                    <Input data-testid={`swap-custom-${k}`} type="number" min="0" value={customFood[k]}
                      onChange={(e) => setCustomFood({ ...customFood, [k]: e.target.value })}
                      className="bg-black border-white/15 text-white" />
                  </div>
                ))}
              </div>
              <Button data-testid="swap-custom-confirm" onClick={doSwapCustom}
                className="w-full rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold">
                <Plus className="h-3.5 w-3.5 mr-1" /> Use this food
              </Button>
              <p className="text-[10px] text-zinc-300">Your macros go straight in — the day's totals recalculate but nothing else auto-rebalances.</p>
            </div>
          ) : (
          <div className="max-h-[55vh] overflow-y-auto space-y-2 mt-2">
            {loadingAlts ? (
              <div className="flex items-center justify-center py-8 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : alts.length === 0 ? (
              <p className="text-sm text-zinc-400 py-6 text-center">No other exclusion-safe options for this slot.</p>
            ) : alts.map((a) => (
              <button key={a.id} data-testid={`swap-option-${a.id}`} disabled={swapping}
                onClick={() => doSwap(a.id)}
                className="w-full text-left border border-white/10 hover:border-[#E2E30A] p-3 transition-colors disabled:opacity-50">
                <p className="text-sm text-white">{a.name}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5">{(a.ingredients || []).map((ing) => `${ing.name} ${ing.qty}${ing.unit}`).join(" · ")}</p>
                <p className="text-[10px] text-zinc-300 font-mono mt-0.5">{Math.round(a.kcal)}kcal · {Math.round(a.protein)}P {Math.round(a.carb)}C {Math.round(a.fat)}F <span className="text-zinc-300">(base portion, before scaling)</span></p>
              </button>
            ))}
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
